import {isVideoAdSource,adSourceDownloadUrl} from '@shared/adSourceMedia';
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useVideoEnabled, useVideoGateCopy } from '@/hooks/use-video-enabled';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Masonry from 'react-masonry-css';
import {
  Search,
  X,
  Download,
  Upload,
  Loader2,
  Trash2,
  Plus,
  FolderArchive,
  Filter,
  Layers,
  Palette,
  LayoutGrid,
  List,
  ArrowUpDown,
  Check,
  Copy,
  Eye,
  ChevronDown,
  ArrowLeft,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useCanvasStore } from './canvas-store';
import type { CanvasView } from './canvas-types';
import type { AdTemplate } from '@shared/schema';
import { CloneDestinationPicker } from './canvas-clone-picker';
import { runVideoClonePrepare } from './video-clone-runner';

type ActiveFilter = { category: string; value: string; label: string };
type SortOption = 'newest' | 'oldest' | 'name_asc' | 'name_desc';

const NICHE_OPTIONS = [
  'Ecommerce', 'Nutra', 'Skincare', 'Weightloss', 'Fashion', 'SaaS',
  'Food & Beverage', 'Fitness', 'Health', 'Beauty', 'Tech', 'Finance',
  'Education', 'Travel', 'Real Estate', 'Automotive', 'Entertainment',
  'Gaming', 'Pets', 'Home & Garden', 'Sports', 'Luxury', 'B2B', 'Agency',
  'Supplements', 'CBD', 'Dental', 'Hair Care', 'Anti-Aging', 'Wellness', 'UGC',
];

const STYLE_OPTIONS = [
  'Minimalist', 'Bold', 'Colorful', 'Dark', 'Light', 'Gradient',
  'Photo-centric', 'Text-heavy', 'Product-focused', 'Lifestyle',
  'Before-After', 'Testimonial', 'Comparison', 'UGC-style', 'Premium',
  'Playful', 'Corporate', 'Retro', 'Modern', 'Neon', 'Natural', 'Clean',
  'Split-layout', 'Grid', 'Collage', 'Flat-design', '3D', 'Illustrated',
];

const FORMAT_OPTIONS = ['Square', 'Portrait', 'Landscape', 'Story', 'Banner'];

const masonryBreakpoints = {
  default: 3,
  1200: 3,
  900: 2,
  600: 1,
};

/**
 * IntersectionObserver-backed lazy image.
 *
 * The browser's native `loading="lazy"` is mostly ignored for thumbnails in a
 * grid — anything within a viewport or two of visible gets fetched. With 140
 * templates × ~500KB each that's a lot of bandwidth on first load.
 *
 * This wrapper renders a same-aspect-ratio placeholder until the element
 * scrolls within `rootMargin` of the viewport, then mounts the real <img>.
 * Once shown, it stays mounted (no flicker on scroll-out/in).
 */
function LazyImage({
  src,
  alt,
  style,
  className,
  rootMargin = "300px",
  placeholderColor = "#F0EDE9",
  onError,
  "data-testid": dataTestId,
}: {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  className?: string;
  rootMargin?: string;
  placeholderColor?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  "data-testid"?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const node = wrapRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [inView, rootMargin]);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        position: "relative",
        background: placeholderColor,
        minHeight: 80,
        ...style,
      }}
    >
      {inView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          style={{ width: "100%", height: "auto", display: "block" }}
          onError={onError}
          data-testid={dataTestId}
        />
      )}
    </div>
  );
}

/** 8 → "0:08", 75 → "1:15" — duration badge on video template cards. */
function formatVideoDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function TemplateCard({
  template,
  onPreview,
  onDelete,
  isSelected,
  onSelect,
}: {
  template: AdTemplate;
  onPreview: () => void;
  onDelete: () => void;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      data-testid={`card-template-${template.id}`}
      style={{
        marginBottom: 12,
        borderRadius: 10,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        background: '#000',
        border: isSelected ? '2px solid #6366F1' : '1px solid #E5E5E3',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.10)' : '0 1px 4px rgba(0,0,0,0.05)',
        transition: 'box-shadow 150ms, border-color 150ms',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelect}
    >
      <div style={{ position: 'relative' }}>
        <LazyImage
          src={template.imageUrl}
          alt={template.name}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />

        {/* Bottom overlay: name + tags */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '24px 10px 8px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)',
            pointerEvents: 'none',
          }}
        >
          {template.name && (
            <p
              data-testid={`text-template-name-${template.id}`}
              style={{
                fontSize: 11,
                fontWeight: 500,
                color: '#FFFFFF',
                margin: '0 0 3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textShadow: '0 1px 3px rgba(0,0,0,0.6)',
              }}
            >
              {template.name}
            </p>
          )}
          {template.niche && template.niche.length > 0 && (
            <p style={{ fontSize: 10, color: '#A0A0A0', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {template.niche.slice(0, 3).join(' · ')}
            </p>
          )}
        </div>

        {/* Top-right hover actions */}
        {hovered && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 4,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <ActionIcon title="Preview" onClick={onPreview}>
              <Eye style={{ width: 12, height: 12 }} />
            </ActionIcon>
            <ActionIcon
              title="Download"
              onClick={() => {
                const url = adSourceDownloadUrl(template);
                if (!url) return;
                const a = document.createElement('a');
                a.href = url;
                a.download = template.name;
                a.click();
              }}
            >
              <Download style={{ width: 12, height: 12 }} />
            </ActionIcon>
            <ActionIcon title="Delete" danger onClick={onDelete}>
              <Trash2 style={{ width: 12, height: 12 }} />
            </ActionIcon>
          </div>
        )}

        {/* Top-left video chip + duration badge (drops below the selection
            checkmark when the card is selected) */}
        {template.mediaType === 'video' && (
          <div
            data-testid={`chip-video-template-${template.id}`}
            style={{ position: 'absolute', top: isSelected ? 34 : 8, left: 8, display: 'flex', gap: 4, pointerEvents: 'none' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 10, background: 'rgba(0,0,0,0.65)', color: '#FFFFFF', fontSize: 10, fontWeight: 600, backdropFilter: 'blur(4px)' }}>
              Video
            </span>
            {template.videoDurationSec != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 10, background: 'rgba(0,0,0,0.65)', color: '#E5E2DC', fontSize: 10, fontWeight: 600, backdropFilter: 'blur(4px)', fontVariantNumeric: 'tabular-nums' }}>
                {formatVideoDuration(template.videoDurationSec)}
              </span>
            )}
          </div>
        )}

        {/* Top-left selection checkmark */}
        {isSelected && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: '#6366F1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Check style={{ width: 11, height: 11, color: '#FFFFFF' }} />
          </div>
        )}
      </div>
    </div>
  );
}

function ActionIcon({
  children,
  title,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        width: 26,
        height: 26,
        borderRadius: 6,
        background: danger ? 'rgba(180,20,20,0.80)' : 'rgba(0,0,0,0.55)',
        color: '#FFFFFF',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        backdropFilter: 'blur(4px)',
      }}
    >
      {children}
    </button>
  );
}

function DetailModal({
  template,
  onClose,
  onClone,
  onDelete,
  onUseAsReference,
}: {
  template: AdTemplate;
  onClose: () => void;
  onClone: () => void;
  onDelete: () => void;
  onUseAsReference: () => void;
}) {
  // v7.04 — video cloning is admin-only while the feature is being built.
  const videoEnabled = useVideoEnabled();
  const videoGateCopy = useVideoGateCopy();
  const videoLocked = template.mediaType === 'video' && !videoEnabled;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FAFAF8',
          border: '1px solid #E5E2DC',
          borderRadius: 16,
          width: '90%',
          maxWidth: 780,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #E5E2DC' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>{template.name}</span>
          <button
            onClick={onClose}
            style={{ width: 28, height: 28, borderRadius: 8, background: 'transparent', color: '#8C8578', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EDEDEA')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
          >
            <X style={{ width: 15, height: 15 }} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
          <div style={{ flex: 1, background: '#F0F0EF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <img
              src={template.imageUrl}
              alt={template.name}
              style={{ maxWidth: '100%', maxHeight: '55vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}
              data-testid={`img-template-preview-${template.id}`}
            />
          </div>
          <div style={{ width: 240, flexShrink: 0, borderLeft: '1px solid #E5E2DC', padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Format</span>
              <p style={{ fontSize: 13, color: '#3D3A36', marginTop: 4, textTransform: 'capitalize' }}>{template.format || '—'}</p>
            </div>
            {template.niche && template.niche.length > 0 && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Niche</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                  {template.niche.map((n) => (
                    <span key={n} style={{ fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 20, background: '#F0EDE8', color: '#6B6760', border: '1px solid #E5E2DC' }}>{n}</span>
                  ))}
                </div>
              </div>
            )}
            {template.styleTags && template.styleTags.length > 0 && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Style</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                  {template.styleTags.map((s) => (
                    <span key={s} style={{ fontSize: 11, fontWeight: 500, padding: '3px 8px', borderRadius: 20, background: '#F0EDE8', color: '#6B6760', border: '1px solid #E5E2DC' }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
            {template.description && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#8C8578', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Description</span>
                <p style={{ fontSize: 12.5, color: '#6B6760', marginTop: 4, lineHeight: 1.5 }}>{template.description}</p>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #E5E2DC', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            data-testid="button-canvas-delete-template"
            onClick={onDelete}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #F5C6C6', background: 'transparent', color: '#DC2626', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FEF2F2')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
          >
            <Trash2 style={{ width: 13, height: 13 }} />
            Delete
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              data-testid="button-canvas-use-as-reference"
              onClick={onUseAsReference}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #E5E2DC', background: '#FFFFFF', color: '#3D3A36', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF')}
            >
              <Eye style={{ width: 13, height: 13 }} />
              Use as reference
            </button>
            <button
              data-testid="button-canvas-clone-template"
              onClick={() => { if (!videoLocked) onClone(); }}
              disabled={videoLocked}
              title={videoLocked ? videoGateCopy.long : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #E5E2DC', background: videoLocked ? '#F5F3EE' : '#FFFFFF', color: videoLocked ? '#B5B0A8' : '#3D3A36', fontSize: 12.5, fontWeight: 500, cursor: videoLocked ? 'default' : 'pointer' }}
              onMouseEnter={(e) => { if (!videoLocked) (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
              onMouseLeave={(e) => { if (!videoLocked) (e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF'; }}
            >
              <Copy style={{ width: 13, height: 13 }} />
              {videoLocked ? videoGateCopy.short : template.mediaType === 'video' ? 'Clone this video ad' : 'Clone this ad'}
            </button>
            <a
              href={adSourceDownloadUrl(template)} aria-disabled={!adSourceDownloadUrl(template)}
              download={template.name}
              data-testid="button-canvas-download-template"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', textDecoration: 'none' }}
            >
              <Download style={{ width: 13, height: 13 }} />
              Download
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadData, setUploadData] = useState({ name: '', description: '', niche: [] as string[], format: 'square', styleTags: [] as string[] });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleFile = useCallback(async (f: File) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await fetch('/api/ad-templates/analyze', { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Analysis failed');
      const result = await res.json();
      setUploadData({
        name: result.name || '',
        description: result.description || '',
        niche: result.niche || [],
        format: result.format || 'square',
        styleTags: result.styleTags || [],
      });
    } catch {
      toast({ title: 'Auto-analysis failed', description: 'You can fill in the details manually.', variant: 'destructive' });
    } finally {
      setAnalyzing(false);
    }
  }, [toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.type.startsWith('image/')) handleFile(f);
  }, [handleFile]);

  const handleSubmit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', uploadData.name || 'Untitled Template');
      formData.append('description', uploadData.description || '');
      formData.append('niche', JSON.stringify(uploadData.niche));
      formData.append('format', uploadData.format);
      formData.append('styleTags', JSON.stringify(uploadData.styleTags));
      formData.append('uploadedBy', 'user');
      const res = await fetch('/api/ad-templates', { method: 'POST', body: formData });
      if (!res.ok) throw new Error((await res.json()).message);
      queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] });
      toast({ title: 'Template uploaded successfully' });
      onSuccess();
      onClose();
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#FAFAF8', border: '1px solid #E5E2DC', borderRadius: 16, width: '90%', maxWidth: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #E5E2DC' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>Upload Template</span>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: 'transparent', color: '#8C8578', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EDEDEA')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
          >
            <X style={{ width: 15, height: 15 }} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {!file ? (
            <div
              data-testid="dropzone-upload-template"
              style={{
                border: `2px dashed ${isDragOver ? '#8C8578' : '#D0CECA'}`,
                borderRadius: 12,
                padding: '40px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: isDragOver ? '#F5F3EE' : '#F7F6F3',
                cursor: 'pointer',
                transition: 'all 150ms',
              }}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload style={{ width: 28, height: 28, color: '#8C8578' }} />
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#3D3A36' }}>Drop an image here or click to browse</p>
                <p style={{ fontSize: 12, color: '#8C8578', marginTop: 4 }}>PNG, JPG, WebP — AI will auto-categorize</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                {preview && (
                  <img src={preview} alt="Preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E2DC', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1 }}>
                  {analyzing && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#F0EDE8', borderRadius: 8, marginBottom: 10 }}>
                      <Loader2 style={{ width: 14, height: 14, color: '#8C8578' }} className="animate-spin" />
                      <span style={{ fontSize: 12.5, color: '#6B6760' }}>AI is categorizing your template…</span>
                    </div>
                  )}
                  <label style={{ fontSize: 12, fontWeight: 500, color: '#6B6760', display: 'block', marginBottom: 4 }}>Name</label>
                  <input
                    data-testid="input-canvas-template-name"
                    value={uploadData.name}
                    onChange={(e) => setUploadData((d) => ({ ...d, name: e.target.value }))}
                    placeholder="Template name"
                    style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #E0DDD8', background: '#F7F6F3', fontSize: 13, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#6B6760', display: 'block', marginBottom: 4 }}>Format</label>
                <select
                  value={uploadData.format}
                  onChange={(e) => setUploadData((d) => ({ ...d, format: e.target.value }))}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #E0DDD8', background: '#F7F6F3', fontSize: 13, color: '#1A1A1A', outline: 'none' }}
                >
                  {FORMAT_OPTIONS.map((f) => <option key={f} value={f.toLowerCase()}>{f}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: '#6B6760', display: 'block', marginBottom: 4 }}>Description</label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData((d) => ({ ...d, description: e.target.value }))}
                  placeholder="Optional description"
                  rows={2}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #E0DDD8', background: '#F7F6F3', fontSize: 13, color: '#1A1A1A', outline: 'none', resize: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid #E5E2DC', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #E0DDD8', background: 'transparent', color: '#6B6760', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
          >
            Cancel
          </button>
          <button
            data-testid="button-canvas-upload-submit"
            onClick={handleSubmit}
            disabled={!file || uploading || analyzing}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: !file || uploading || analyzing ? '#D0CECA' : '#1A1A1A', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: !file || uploading || analyzing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {uploading && <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />}
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

type ThumbStatus = 'pending' | 'extracting' | 'categorizing' | 'done' | 'failed';
interface ThumbItem { name: string; status: ThumbStatus; }

function ThumbStatusIcon({ status }: { status: ThumbStatus }) {
  if (status === 'done') return <Check style={{ width: 11, height: 11, color: '#16a34a' }} />;
  if (status === 'failed') return <X style={{ width: 11, height: 11, color: '#DC2626' }} />;
  if (status === 'categorizing' || status === 'extracting') return <Loader2 style={{ width: 11, height: 11, color: '#8C8578' }} className="animate-spin" />;
  return <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#E5E2DC' }} />;
}

function ZipUploadModal({ onClose }: { onClose: () => void }) {
  const [uploading, setUploading] = useState(false);
  const [thumbs, setThumbs] = useState<ThumbItem[]>([]);
  const [result, setResult] = useState<{ success: number; failed: number } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const simulateThumbs = useCallback((count: number, successCount: number) => {
    const items: ThumbItem[] = Array.from({ length: count }, (_, i) => ({
      name: `image_${i + 1}.jpg`,
      status: 'pending' as ThumbStatus,
    }));
    setThumbs(items);

    let idx = 0;
    const interval = setInterval(() => {
      if (idx >= count) { clearInterval(interval); return; }
      const i = idx;
      idx++;
      setThumbs((prev) => prev.map((t, j) => j === i ? { ...t, status: 'extracting' } : t));
      setTimeout(() => {
        setThumbs((prev) => prev.map((t, j) => j === i ? { ...t, status: 'categorizing' } : t));
        setTimeout(() => {
          const isFailed = i >= successCount;
          setThumbs((prev) => prev.map((t, j) => j === i ? { ...t, status: isFailed ? 'failed' : 'done' } : t));
        }, 600);
      }, 400);
    }, 150);
  }, []);

  const handleFile = async (file: File) => {
    setUploading(true);
    setResult(null);
    setThumbs([{ name: 'Uploading ZIP…', status: 'extracting' }]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/ad-templates/batch', { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Batch upload failed');
      }
      const r = await res.json();
      const total = (r.success || 0) + (r.failed || 0);
      simulateThumbs(Math.max(total, 1), r.success || 0);
      setTimeout(() => {
        setResult({ success: r.success, failed: r.failed });
        queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] });
        toast({ title: 'Batch upload complete', description: `${r.success} template${r.success !== 1 ? 's' : ''} added${r.failed > 0 ? `, ${r.failed} failed` : ''}` });
      }, total * 150 + 1200);
    } catch (err: any) {
      setThumbs([]);
      toast({ title: 'Batch upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const phase = thumbs.length > 0 && !result;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#FAFAF8', border: '1px solid #E5E2DC', borderRadius: 16, width: '90%', maxWidth: 480, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #E5E2DC', flexShrink: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>Upload ZIP</span>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: 'transparent', color: '#8C8578', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#EDEDEA')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
          >
            <X style={{ width: 15, height: 15 }} />
          </button>
        </div>
        <div style={{ padding: '24px 20px', flex: 1, overflowY: 'auto' }}>
          <input ref={fileInputRef} type="file" accept=".zip" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          {!uploading && !phase && !result && (
            <div
              data-testid="dropzone-upload-zip"
              style={{
                border: `2px dashed ${isDragOver ? '#8C8578' : '#D0CECA'}`,
                borderRadius: 12,
                padding: '40px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: isDragOver ? '#F5F3EE' : '#F7F6F3',
                cursor: 'pointer',
                transition: 'all 150ms',
              }}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FolderArchive style={{ width: 28, height: 28, color: '#8C8578' }} />
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#3D3A36' }}>Drop a ZIP file here or click to browse</p>
                <p style={{ fontSize: 12, color: '#8C8578', marginTop: 4 }}>All images will be extracted and AI-categorized</p>
              </div>
            </div>
          )}
          {phase && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <Loader2 style={{ width: 16, height: 16, color: '#8C8578' }} className="animate-spin" />
                <span style={{ fontSize: 13.5, color: '#3D3A36', fontWeight: 500 }}>Extracting and categorizing…</span>
              </div>
              <div style={{ width: '100%', height: 4, background: '#E5E2DC', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ height: '100%', background: '#1A1A1A', borderRadius: 2, animation: 'progress-indeterminate 1.5s ease-in-out infinite', width: '40%' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {thumbs.map((t, i) => (
                  <div key={i} data-testid={`zip-thumb-item-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 8, background: '#F7F6F3', border: '1px solid #E5E2DC' }}>
                    <ThumbStatusIcon status={t.status} />
                    <span style={{ flex: 1, fontSize: 12.5, color: '#3D3A36', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                    <span style={{ fontSize: 11, color: '#8C8578', flexShrink: 0, textTransform: 'capitalize' }}>{t.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <p style={{ fontSize: 20, fontWeight: 700, color: '#1A1A1A' }}>{result.success} template{result.success !== 1 ? 's' : ''} added</p>
              {result.failed > 0 && <p style={{ fontSize: 13, color: '#8C8578', marginTop: 4 }}>{result.failed} failed to process</p>}
              <button
                onClick={onClose}
                style={{ marginTop: 20, padding: '9px 20px', borderRadius: 10, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterDropdown({
  activeFilters,
  onAdd,
  onClose,
}: {
  activeFilters: ActiveFilter[];
  onAdd: (category: string, value: string, label: string) => void;
  onClose: () => void;
}) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const categories = [
    { key: 'Niche', icon: <Layers style={{ width: 12, height: 12 }} />, options: NICHE_OPTIONS },
    { key: 'Format', icon: <LayoutGrid style={{ width: 12, height: 12 }} />, options: FORMAT_OPTIONS },
    { key: 'Style', icon: <Palette style={{ width: 12, height: 12 }} />, options: STYLE_OPTIONS },
  ];

  const activeCat = categories.find((c) => c.key === activeCategory);
  const filteredOptions = activeCat
    ? activeCat.options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : [];

  return (
    <div
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        zIndex: 200,
        background: '#FFFFFF',
        border: '1px solid #E5E2DC',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        minWidth: 320,
        overflow: 'hidden',
      }}
    >
      <div style={{ width: 130, borderRight: '1px solid #F0EDE8', padding: '6px 0' }}>
        {categories.map((cat) => (
          <button
            key={cat.key}
            onClick={() => { setActiveCategory(cat.key); setSearch(''); }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              fontSize: 12.5,
              color: activeCategory === cat.key ? '#1A1A1A' : '#3D3A36',
              background: activeCategory === cat.key ? '#F5F3EE' : 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontWeight: activeCategory === cat.key ? 500 : 400,
              textAlign: 'left',
            }}
          >
            <span style={{ color: '#8C8578' }}>{cat.icon}</span>
            {cat.key}
          </button>
        ))}
      </div>
      {activeCategory && activeCat && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', maxHeight: 280 }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #F0EDE8' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${activeCategory.toLowerCase()}…`}
              style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #E0DDD8', background: '#F7F6F3', fontSize: 12, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }}
              autoFocus
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
            {filteredOptions.map((opt) => {
              const isActive = activeFilters.some((f) => f.category === activeCategory && f.value === opt);
              return (
                <button
                  key={opt}
                  onClick={() => { onAdd(activeCategory, opt, opt); }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 12px',
                    fontSize: 12.5,
                    color: '#3D3A36',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                >
                  <span>{opt}</span>
                  {isActive && <Check style={{ width: 12, height: 12, color: '#1A1A1A', flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function CanvasAdTemplatesView() {
  const { setActiveView, sendMessage, previousView } = useCanvasStore();
  const { toast } = useToast();
  // v7.04 — video cloning is admin-only while the feature is being built.
  const videoEnabled = useVideoEnabled();
  const videoGateCopy = useVideoGateCopy();
  const queryClient = useQueryClient();

  const handleBack = () => {
    const fallback: CanvasView = 'chat-only';
    setActiveView(previousView && previousView !== 'ad-templates' ? previousView : fallback);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AdTemplate | null>(null);
  const [clonePickerTemplate, setClonePickerTemplate] = useState<AdTemplate | null>(null);
  const [cloneQueue, setCloneQueue] = useState<AdTemplate[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showZipModal, setShowZipModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const MAX_SELECTION = 5;

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SELECTION) return prev;
      return [...prev, id];
    });
  };

  const advanceCloneQueue = () => {
    setCloneQueue((prev) => {
      const next = prev.slice(1);
      if (next.length > 0) {
        setClonePickerTemplate(next[0]);
      } else {
        setClonePickerTemplate(null);
      }
      return next;
    });
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: templates = [], isLoading } = useQuery<AdTemplate[]>({
    queryKey: ['/api/ad-templates'],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/ad-templates/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] });
      toast({ title: 'Template deleted' });
      setSelectedTemplate(null);
    },
    onError: () => toast({ title: 'Delete failed', variant: 'destructive' }),
  });

  const addFilter = useCallback((category: string, value: string, label: string) => {
    setActiveFilters((prev) => {
      if (prev.some((f) => f.category === category && f.value === value)) return prev;
      return [...prev, { category, value, label }];
    });
    setShowFilterDropdown(false);
  }, []);

  const removeFilter = useCallback((category: string, value: string) => {
    setActiveFilters((prev) => prev.filter((f) => !(f.category === category && f.value === value)));
  }, []);

  const filteredTemplates = useMemo(() => {
    let results = templates;
    const nicheFilters = activeFilters.filter((f) => f.category === 'Niche').map((f) => f.value);
    const formatFilters = activeFilters.filter((f) => f.category === 'Format').map((f) => f.value.toLowerCase());
    const styleFilters = activeFilters.filter((f) => f.category === 'Style').map((f) => f.value);
    if (nicheFilters.length > 0) results = results.filter((t) => (t.niche || []).some((n) => nicheFilters.includes(n)));
    if (formatFilters.length > 0) results = results.filter((t) => formatFilters.includes((t.format || '').toLowerCase()));
    if (styleFilters.length > 0) results = results.filter((t) => (t.styleTags || []).some((s) => styleFilters.includes(s)));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      results = results.filter((t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        (t.niche || []).some((n) => n.toLowerCase().includes(q)) ||
        (t.styleTags || []).some((s) => s.toLowerCase().includes(q))
      );
    }
    return [...results].sort((a, b) => {
      switch (sortBy) {
        case 'oldest': return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case 'name_asc': return a.name.localeCompare(b.name);
        case 'name_desc': return b.name.localeCompare(a.name);
        default: return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      }
    });
  }, [templates, searchQuery, activeFilters, sortBy]);

  const handleUseAsReference = (template: AdTemplate) => {
    if (isVideoAdSource(template)) {
      toast({title:'Video ad',description:'Use the video clone flow. A video poster cannot be used as an image ad reference.'});
      return;
    }
    // v6.62 — with no open task the message goes to a null transcript and
    // silently vanishes (while still billing the chat turn). Block instead.
    if (useCanvasStore.getState().activeProjectId == null) {
      toast({ title: 'Open a task first', description: 'References are sent to a task\'s chat — open the task (under its brand) you want to use it in.', variant: 'destructive' });
      return;
    }
    sendMessage('Use this template as reference', template.imageUrl);
    toast({ title: 'Sent to canvas chat', description: `"${template.name}" added as reference.` });
    setSelectedTemplate(null);
  };

  const handleClone = (template: AdTemplate) => {
    // v7.04 — video cloning is admin-only while the feature is being built.
    // Guarding the handler (not just the buttons) covers every entry point
    // into the picker; the server 403s the routes regardless.
    if (isVideoAdSource(template) && !videoEnabled) {
      toast({ title: videoGateCopy.long, description: 'Image ads are ready to go — clone any image ad instead.' });
      return;
    }
    // Image and video templates share the picker state; the picker render
    // site branches on mediaType (video → project-selection-only mode that
    // fires the video-clone prepare runner instead of the image-drop flow).
    setClonePickerTemplate(isVideoAdSource(template) ? {...template,mediaType:'video'} : template);
    setSelectedTemplate(null);
  };

  /**
   * Video-clone step 2: the picker handed back a projectId (it already
   * switchProject'd to it). Resolve the project's brand — the prepare route
   * requires brandId — then fire the SSE prepare runner. The runner opens the
   * VideoScriptDoc panel and drops storyboard frames on the canvas as they
   * stream in.
   */
  const handleVideoClonePicked = async (template: AdTemplate, projectId: number) => {
    const { setActiveView: setView, setCanvasOpen } = useCanvasStore.getState();
    setView('chat-canvas');
    setCanvasOpen(true);
    let brandId: number | null = null;
    try {
      const resp = await fetch(`/api/canvas/projects/${projectId}`);
      if (resp.ok) {
        const proj = await resp.json();
        brandId = proj.brandId ?? proj.brand_id ?? null;
      }
    } catch (err) {
      console.warn('[VideoClone] failed to resolve brand for project', projectId, err);
    }
    if (!brandId) {
      toast({
        title: 'No brand on this task',
        description: 'Video clone needs a brand — pick a task that belongs to a brand.',
        variant: 'destructive',
      });
      return;
    }
    useCanvasStore.setState({ activeBrandId: brandId });
    runVideoClonePrepare({ templateId: template.id, brandId, projectId }).catch((err) => {
      console.error('[VideoClone] prepare failed:', err);
    });
  };

  const sortLabels: Record<SortOption, string> = { newest: 'Newest', oldest: 'Oldest', name_asc: 'Name A-Z', name_desc: 'Name Z-A' };

  return (
    <div
      data-testid="canvas-ad-templates-view"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#F0F0EF', fontFamily: 'system-ui, sans-serif' }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) { setShowUploadModal(true); }
          e.target.value = '';
        }}
      />

      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E2DC', padding: '12px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <button
            data-testid="button-canvas-ad-templates-back"
            onClick={handleBack}
            style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #E5E2DC', background: '#FFFFFF', color: '#3D3A36', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE')}
            onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF')}
            title="Back"
          >
            <ArrowLeft style={{ width: 15, height: 15 }} />
          </button>

          <div style={{ flex: 1, position: 'relative' }}>
            <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#8C8578', pointerEvents: 'none' }} />
            <input
              data-testid="input-canvas-search-templates"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search keywords, niches, styles…"
              style={{
                width: '100%',
                padding: '8px 32px 8px 32px',
                borderRadius: 20,
                border: '1px solid #E5E2DC',
                background: '#F7F6F3',
                fontSize: 13,
                color: '#1A1A1A',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#B5B0A8')}
              onBlur={(e) => (e.currentTarget.style.borderColor = '#E5E2DC')}
            />
            {searchQuery && (
              <button
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                onClick={() => setSearchQuery('')}
              >
                <X style={{ width: 13, height: 13, color: '#8C8578' }} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              data-testid="button-canvas-upload-zip"
              onClick={() => setShowZipModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 20, border: '1px solid #E5E2DC', background: '#FFFFFF', color: '#3D3A36', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF')}
            >
              <FolderArchive style={{ width: 13, height: 13 }} />
              Upload ZIP
            </button>
            <button
              data-testid="button-canvas-upload-template"
              onClick={() => setShowUploadModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 20, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              <Upload style={{ width: 13, height: 13 }} />
              Upload
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', position: 'relative' }}>
            <button
              data-testid="button-canvas-add-filter"
              onClick={() => { setShowFilterDropdown((v) => !v); setShowSortDropdown(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: '1px solid transparent', background: 'transparent', color: '#6B6760', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
            >
              <Filter style={{ width: 12, height: 12 }} />
              Add Filter
            </button>
            {showFilterDropdown && (
              <FilterDropdown activeFilters={activeFilters} onAdd={addFilter} onClose={() => setShowFilterDropdown(false)} />
            )}
            {activeFilters.map((filter) => (
              <span
                key={`${filter.category}-${filter.value}`}
                data-testid={`chip-filter-${filter.category.toLowerCase()}-${filter.value.toLowerCase().replace(/\s+/g, '-')}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: '#F0EDE8', border: '1px solid #E5E2DC', fontSize: 12, color: '#3D3A36', fontWeight: 500 }}
              >
                <span style={{ color: '#8C8578', fontSize: 10 }}>{filter.category}</span>
                <span style={{ color: '#D0CECA' }}>|</span>
                {filter.label}
                <button
                  onClick={() => removeFilter(filter.category, filter.value)}
                  style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#8C8578' }}
                  data-testid={`button-remove-filter-${filter.value.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <X style={{ width: 11, height: 11 }} />
                </button>
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }}>
            <div style={{ display: 'flex', border: '1px solid #E5E2DC', borderRadius: 6, overflow: 'hidden' }}>
              <button
                data-testid="button-canvas-view-grid"
                onClick={() => setViewMode('grid')}
                title="Grid view"
                style={{ width: 28, height: 26, border: 'none', background: viewMode === 'grid' ? '#F0EDE8' : '#FFFFFF', color: viewMode === 'grid' ? '#1A1A1A' : '#8C8578', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <LayoutGrid style={{ width: 13, height: 13 }} />
              </button>
              <button
                data-testid="button-canvas-view-list"
                onClick={() => setViewMode('list')}
                title="List view"
                style={{ width: 28, height: 26, border: 'none', borderLeft: '1px solid #E5E2DC', background: viewMode === 'list' ? '#F0EDE8' : '#FFFFFF', color: viewMode === 'list' ? '#1A1A1A' : '#8C8578', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <List style={{ width: 13, height: 13 }} />
              </button>
            </div>
            <button
              data-testid="button-canvas-sort"
              onClick={() => { setShowSortDropdown((v) => !v); setShowFilterDropdown(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: '1px solid #E5E2DC', background: '#FFFFFF', color: '#6B6760', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
              onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FFFFFF')}
            >
              <ArrowUpDown style={{ width: 12, height: 12 }} />
              {sortLabels[sortBy]}
              <ChevronDown style={{ width: 11, height: 11 }} />
            </button>
            {showSortDropdown && (
              <div
                style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 200, background: '#FFFFFF', border: '1px solid #E5E2DC', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', padding: '4px 0', minWidth: 140 }}
              >
                {(['newest', 'oldest', 'name_asc', 'name_desc'] as SortOption[]).map((opt) => (
                  <button
                    key={opt}
                    data-testid={`sort-option-canvas-${opt}`}
                    onClick={() => { setSortBy(opt); setShowSortDropdown(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 14px', fontSize: 12.5, color: '#3D3A36', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                  >
                    <span>{sortLabels[opt]}</span>
                    {sortBy === opt && <Check style={{ width: 12, height: 12, color: '#1A1A1A' }} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }} onClick={() => { setShowFilterDropdown(false); setShowSortDropdown(false); }}>
        <div style={{ marginBottom: 14, fontSize: 13.5, fontWeight: 600, color: '#1A1A1A' }} data-testid="text-canvas-template-count">
          {filteredTemplates.length} Template{filteredTemplates.length !== 1 ? 's' : ''}
          {activeFilters.length > 0 && <span style={{ fontWeight: 400, color: '#8C8578' }}> (filtered)</span>}
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
            <Loader2 style={{ width: 28, height: 28, color: '#8C8578' }} className="animate-spin" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#F0EDE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {templates.length === 0 ? <Upload style={{ width: 22, height: 22, color: '#8C8578' }} /> : <Search style={{ width: 22, height: 22, color: '#8C8578' }} />}
            </div>
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#1A1A1A' }}>{templates.length === 0 ? 'No templates yet' : 'No templates found'}</p>
              <p style={{ fontSize: 13, color: '#8C8578', marginTop: 4 }}>
                {templates.length === 0 ? 'Upload your first ad template to get started.' : 'Try different search terms or adjust your filters.'}
              </p>
            </div>
            {templates.length === 0 && (
              <button
                onClick={() => setShowUploadModal(true)}
                style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#1A1A1A', color: '#FFFFFF', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Upload style={{ width: 14, height: 14 }} />
                Upload Template
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <Masonry
            breakpointCols={masonryBreakpoints}
            className="canvas-masonry-grid"
            columnClassName="canvas-masonry-column"
          >
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onPreview={() => setSelectedTemplate(template)}
                onDelete={() => { if (confirm('Delete this template?')) deleteMutation.mutate(template.id); }}
                isSelected={selectedIds.includes(template.id)}
                onSelect={() => toggleSelection(template.id)}
              />
            ))}
          </Masonry>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                data-testid={`row-template-${template.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px', borderRadius: 10, border: '1px solid #E5E2DC', background: '#FFFFFF', cursor: 'pointer', transition: 'box-shadow 150ms' }}
                onClick={() => setSelectedTemplate(template)}
                onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.boxShadow = 'none')}
              >
                <LazyImage
                  src={template.imageUrl}
                  alt={template.name}
                  style={{ width: 52, height: 52, minHeight: 52, borderRadius: 7, border: '1px solid #E5E2DC', flexShrink: 0, overflow: 'hidden' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13.5, fontWeight: 600, color: '#1A1A1A', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{template.name}</p>
                  <p style={{ fontSize: 12, color: '#8C8578', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[template.format, ...(template.niche?.slice(0, 2) ?? []), ...(template.styleTags?.slice(0, 1) ?? [])].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <button
                    title="Clone"
                    onClick={() => handleClone(template)}
                    style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #E5E2DC', background: 'transparent', color: '#6B6760', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                  >
                    <Copy style={{ width: 12, height: 12 }} />
                  </button>
                  <a
                    href={adSourceDownloadUrl(template)} aria-disabled={!adSourceDownloadUrl(template)}
                    download={template.name}
                    title="Download"
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #E5E2DC', background: 'transparent', color: '#6B6760', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.background = '#F5F3EE')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.background = 'transparent')}
                  >
                    <Download style={{ width: 12, height: 12 }} />
                  </a>
                  <button
                    title="Delete"
                    onClick={() => { if (confirm('Delete this template?')) deleteMutation.mutate(template.id); }}
                    style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #E5E2DC', background: 'transparent', color: '#DC2626', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FEF2F2')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                  >
                    <Trash2 style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Multi-select action bar */}
      {selectedIds.length > 0 && (
        <div
          data-testid="action-bar-multi-select"
          style={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            background: '#FFFFFF',
            borderTop: '1px solid #E5E2DC',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 24px',
            zIndex: 100,
            animation: 'slideUpBar 180ms ease-out',
          }}
        >
          <style>{`@keyframes slideUpBar { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
          <span
            data-testid="text-selected-count"
            style={{ fontSize: 13.5, fontWeight: 600, color: '#1A1A1A' }}
          >
            {selectedIds.length} selected
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              data-testid="button-clear-selection"
              onClick={() => setSelectedIds([])}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6B6760', fontWeight: 500, padding: 0 }}
            >
              Clear selection
            </button>
            <button
              data-testid="button-send-to-canvas"
              onClick={() => {
                const selected = templates.filter((t) => selectedIds.includes(t.id));
                if (selected.length === 0) return;
                setSelectedIds([]);
                setCloneQueue(selected);
                setClonePickerTemplate(selected[0]);
              }}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                background: '#6366F1',
                color: '#FFFFFF',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Send to Canvas
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedTemplate && (
        <DetailModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          onClone={() => handleClone(selectedTemplate)}
          onDelete={() => { if (confirm('Delete this template?')) deleteMutation.mutate(selectedTemplate.id); }}
          onUseAsReference={() => handleUseAsReference(selectedTemplate)}
        />
      )}
      {clonePickerTemplate && (
        <CloneDestinationPicker
          imageUrl={clonePickerTemplate.imageUrl}
          imageName={clonePickerTemplate.name}
          {...(clonePickerTemplate.mediaType === 'video'
            ? {
                // Video templates: project selection ONLY. onPicked short-
                // circuits the picker's image-drop/chat-attach handlers; the
                // picker then calls onClose itself, which advances the queue.
                mode: 'video-clone' as const,
                onPicked: (projectId: number) => {
                  const template = clonePickerTemplate;
                  // Defer a tick so the picker's onClose state update settles
                  // before the async prepare flow starts (batch-flow pattern).
                  setTimeout(() => { void handleVideoClonePicked(template, projectId); }, 0);
                },
              }
            : {})}
          onClose={() => {
            if (cloneQueue.length > 1) {
              advanceCloneQueue();
            } else {
              setClonePickerTemplate(null);
              setCloneQueue([]);
            }
          }}
        />
      )}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['/api/ad-templates'] })}
        />
      )}
      {showZipModal && (
        <ZipUploadModal onClose={() => setShowZipModal(false)} />
      )}
    </div>
  );
}
